import React from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import queryString from 'query-string';
import classNames from 'classnames';

import {
    Form,
    Checkbox,
    IconArrow,
    EmailInput,
    StringInput,
    PrimaryButton,
    CondensedTheme,
    PageBehaviourButton,
    AFFILIATE_QUERY_PARAM_KEYS,
} from '@ratehub/base-ui';

import GiftOfferShape from '../definitions/GiftOfferShape';
import getCardOfferTypeAsString from '../functions/getCardOfferTypeAsString';


function CreditCardIncentive({
    cardInfo,
    onAcceptOrDecline,
    isReferralEligible = false,
    className,
}) {
    const intl = useIntl();

    // giftCardOffer may be empty if isReferralEligible is true
    const giftOffer = cardInfo?.giftCardOffer;
    const isGiftOffer = !!giftOffer;
    const offerAmount = giftOffer?.maxDollarValue;
    const offerType = giftOffer?.offerType;
    const incentiveCSS = INCENTIVE_CSS_CLASSES[isGiftOffer ? 0 : 1];

    const hasAffiliateId = typeof window !== 'undefined'
        && queryString.parse(window.location.search)[AFFILIATE_QUERY_PARAM_KEYS.affiliateId];


    function handleAcceptGiftOffer(email, usersName) {
        // naive splitting of usersName into first and last; will not be perfect
        // Example output for the input "Bruce Wayne":
        // matches[0] = "Bruce Wayne" (full match)
        // matches[1] = "Bruce" (first name)
        // matches[2] = " Wayne" (space followed by last name)
        // matches[3] = "Wayne" (last name)
        const matches = usersName.match(/(\w+)(\s(.+))?/);
        const firstName = matches[1];
        const lastName = matches?.[3];

        onAcceptOrDecline({ email, isOfferAccepted: true, firstName, lastName });
    }


    return (
        <Container
            className={classNames('rh-display-flex', 'rh-flex-direction-column', 'rh-align-items-center', className)}
        >
            <div className="incentive-form rh-p-1_25 rh-border-radius-8px rh-box-shadow-surround-large rh-bg-coconut">
                <header>
                    <PageBehaviourButton
                        onClick={() => onAcceptOrDecline({ isOfferAccepted: false })}
                        iconComponent={iconProps => (
                            <IconArrow
                                {...iconProps}
                                outlineWidth="0"
                                direction="right"
                            />
                        )}
                        message={
                            // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                            isGiftOffer
                                ? (
                                    <FormattedMessage
                                        id="edb.cc.incentive.no-thanks"
                                        defaultMessage="Apply without the {offerType}"
                                        values={{ offerType: getCardOfferTypeAsString(giftOffer, intl) }}
                                    />
                                )
                                : (
                                    <FormattedMessage
                                        id="edb.cc.incentive.no-thanks.referral"
                                        defaultMessage="Apply without the referral bonus"
                                    />
                                )
                        }
                        size="extra-small"
                        className="rh-ml-auto rh-mb-1_5"
                        data-name="edb.cc.incentive.no-thanks"
                    />

                    <div className={classNames('header-box rh-position-relative rh-p-1',
                        'rh-border-width-1px rh-border-style-solid rh-border-radius-8px', incentiveCSS.headerBox)}
                    >
                        <If condition={isGiftOffer || isReferralEligible}>
                            <span className={classNames(
                                'expiry-pill rh-position-absolute rh-display-inline-block rh-text-xs rh-px-0_5',
                                'rh-fg-coconut rh-border-radius-8px', incentiveCSS.expiryPill)}
                            >
                                <Choose>
                                    <When condition={isGiftOffer}>
                                        <FormattedMessage
                                            id="edb.cc.incentive.offer-ends"
                                            defaultMessage="Offer ends"
                                        />
                                        <time
                                            className="rh-display-inline-block"
                                            dateTime={giftOffer.expiryDate}
                                            suppressHydrationWarning // prevent hydration warning if server timezone is different than browser timezone
                                        >
                                            <FormattedDate
                                                value={giftOffer.expiryDate}
                                                year="numeric"
                                                month="short"
                                                day="numeric"
                                            />
                                        </time>
                                    </When>
                                    <Otherwise>
                                        <FormattedMessage
                                            id="edb.cc.incentive.referral-bonus"
                                            defaultMessage="Referral bonus"
                                        />
                                    </Otherwise>
                                </Choose>
                            </span>
                        </If>

                        <p className="rh-text-s rh-m-0 rh-mt-0_5 rh-mb-0_25">
                            {cardInfo.name}
                        </p>

                        <h2 className={classNames('rh-title-xs rh-m-0', incentiveCSS.mainTitle)}>
                            <Choose>
                                <When condition={isGiftOffer}>
                                    <FormattedMessage
                                        id="edb.cc.incentive.main-title"
                                        defaultMessage="Get an exclusive {offerAmount, number, CAD0} {offerType}"
                                        values={{ offerAmount, offerType }}
                                    />
                                </When>
                                <Otherwise>
                                    <FormattedMessage
                                        id="edb.cc.incentive.referral.main-title"
                                        defaultMessage="Get $15 when you’re approved for this card!"
                                    />
                                </Otherwise>
                            </Choose>
                        </h2>
                    </div>
                </header>

                <div className="rh-mt-1 rh-p-1">
                    <h2 className="rh-title-xs rh-m-0 rh-mt-0_25">
                        <FormattedMessage
                            id="edb.cc.incentive.claim-your-offer"
                            defaultMessage="Claim your offer"
                        />
                    </h2>

                    <Choose>
                        <When condition={isGiftOffer}>
                            <p
                                className="rh-text-s rh-mt-0_25 rh-mb-0"
                                data-test-name="claim-details"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: giftOffer.promoDetailsHTML,
                                }}
                            />
                        </When>
                        <Otherwise>
                            <p
                                className="rh-text-s rh-mt-0_25 rh-mb-0"
                                data-test-name="claim-details"
                            >
                                <FormattedMessage
                                    id="edb.cc.incentive.referral.promo-details"
                                    defaultMessage="The referral bonus will be sent via Interac e-Transfer® to the email address below upon approval."
                                />
                            </p>
                        </Otherwise>
                    </Choose>

                    <Form
                        className="rh-display-flex rh-flex-wrap rh-mt-1"
                        onSubmit={(values) => handleAcceptGiftOffer(values.email, values.usersName)}
                    >
                        <div className="rh-display-flex rh-flex-direction-column rh-align-items-center">
                            <NameAndEmailFields
                                hasAffiliateId={hasAffiliateId}
                                handleAcceptGiftOffer={handleAcceptGiftOffer}
                            />
                        </div>

                        <div
                            className={classNames('button-wrapper rh-display-flex rh-flex-direction-column rh-mt-2',
                                'rh-width-100p rh-align-items-center rh-gap-0_5')}
                        >
                            <PrimaryButton
                                size="small"
                                className="button-get-offer rh-mt-0 rh-mx-1"
                                data-name="gift-card-offer-submit"
                            >
                                <FormattedMessage
                                    id="edb.cc.incentive.get-offer"
                                    defaultMessage="Get this offer"
                                />
                            </PrimaryButton>

                            <p className="rh-text-xs rh-m-0 rh-fg-blackberry-light">
                                <FormattedMessage
                                    id="edb.cc.incentive.proceed-to-provider"
                                    defaultMessage="Proceed to {providerName}’s secure website"
                                    values={{ providerName: cardInfo.providerName }}
                                />
                            </p>
                        </div>
                    </Form>
                </div>
            </div>
        </Container>
    );
}

CreditCardIncentive.propTypes = {
    cardInfo: PropTypes.shape({
        id: PropTypes.string.isRequired,
        giftCardOffer: GiftOfferShape,
        cardImageURL: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        providerName: PropTypes.string.isRequired,

        signUpBonusPoints: PropTypes.number,
        signUpBonusDollars: PropTypes.number,
        isFirstYearFeeWaived: PropTypes.bool.isRequired,
        annualFee: PropTypes.number.isRequired,
        productType: PropTypes.string.isRequired,
        earningsDescription: PropTypes.array.isRequired,
    }).isRequired,

    onAcceptOrDecline: PropTypes.func.isRequired,
    isReferralEligible: PropTypes.bool,

    className: PropTypes.string,
};


function NameAndEmailFields({ hasAffiliateId }) {
    return (
        <CondensedTheme>
            <div
                className={classNames('two-up', 'rh-display-flex', 'rh-flex-wrap', 'rh-gap-0_5', 'rh-width-100p', {
                    // we will need more white space below this input field wrapper when the subscribe checkbox is omitted
                    'rh-mb-1_5': hasAffiliateId,
                })}
            >
                <StringInput
                    className="side-by-side rh-my-0"
                    name="usersName"
                    label={(
                        <FormattedMessage
                            id="edb.cc.incentive.usersName.label"
                            defaultMessage="Your name"
                        />
                    )}
                    defaultErrorMessage={(
                        <FormattedMessage
                            id="edb.cc.incentive.name-validation.empty"
                            defaultMessage="Please enter your name"
                        />
                    )}
                />

                <EmailInput
                    className="side-by-side rh-my-0"
                    name="email"
                    placeholder={''}
                    label={(
                        <FormattedMessage
                            id="edb.cc.incentive.email.label"
                            defaultMessage="Your email address"
                        />
                    )}
                />
            </div>

            <If condition={!hasAffiliateId}>
                <div className="rh-display-flex rh-align-items-center rh-width-100p rh-my-1_5 rh-mx-0_5">
                    <Checkbox
                        className="checkbox-subscribe"
                        name="subscribe"
                        id="subscribe"
                    />
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                        className="rh-text-s rh-ml-1 rh-my-0"
                        htmlFor="subscribe"
                    >
                        <FormattedMessage
                            id="edb.cc.incentive.newsletters"
                            defaultMessage="I’d like to receive Ratehub credit card offers and newsletters. I may unsubscribe at any time."
                        />
                    </label>
                </div>
            </If>
        </CondensedTheme>
    );
}

NameAndEmailFields.propTypes = {
    hasAffiliateId: PropTypes.string,
};


const INCENTIVE_CSS_CLASSES = [
    // gift offer classes
    {
        headerBox: 'rh-bg-grape-lightest rh-border-color-grape',
        expiryPill: 'rh-bg-grape-dark',
        mainTitle: 'rh-fg-grape-dark',
    },
    // referral bonus classes
    {
        headerBox: 'rh-bg-blueberry-lightest rh-border-color-blueberry-dark',
        expiryPill: 'rh-bg-blueberry-dark',
        mainTitle: 'rh-fg-blueberry-dark',
    },
];


const Container = styled.div`
    > .incentive-form {
        max-width: 90%;
        width: 26em;

        .header-box {
            > .expiry-pill {
                padding-top: 1px;
                top: -0.7em;
                left: 1em;
            }
        }

        .two-up {
            .side-by-side {
                flex: 0 1 100%;
            }
        }

        .button-wrapper {
            .button-get-offer {
                max-width: 15rem;
            }
        }
    }
`;

export default CreditCardIncentive;
